import * as React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { StackChildren, Text } from '@doordash/design-language'
import Loading from '@doordash/component-loading'
import { Interpolate } from '@doordash/lib-i18n'

import t from './strings'
import * as actions from '../../../actions'
import * as events from '../../../events'
import { i18nKeys } from 'translationTypes'
import i18n from '../../../i18n'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import Simple from '../../templates/simple'
import { SimpleStyleType } from '../../templates/simple/constants'
import { Root } from '../../pages/onboarding/styles'
import DasherExcitedAnimation from '../../atoms/dasher-excited'
import Button from '@doordash/component-button'
import LinkButton from '../../atoms/link-button'
import routes from '../../../routes'
import * as DA from '../../../typeModules/events'
import { ApplicationComplete as Tag } from '../../../models/selectorAnchors'

type ApplicationCompleteProps = {}

type ReduxProps = {
  errorMessage?: string
  clearErrors?: () => void
  loadApplication?: (uniqueLink: string) => Promise<void>
  loadDasher?: () => Promise<void>
  application?: {
    firstName: string
  }
  applicantUniqueLink?: string
  dasherId?: string
  track?: (
    action: DA.EventActions,
    name: DA.ApplicationCompleteNames,
    data?: string
  ) => Promise<void>
}

export const ApplicationComplete: React.FC<ApplicationCompleteProps &
  ReduxProps> = ({
  loadDasher,
  loadApplication,
  applicantUniqueLink,
  dasherId,
  application,
  track,
}) => {
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    track(
      DA.EventActions.Rendered,
      DA.ApplicationCompleteNames.ApplicationComplete
    )
  }, [track])

  React.useEffect(() => {
    const loadApplicantData = async () => {
      if (applicantUniqueLink === null) {
        await loadDasher()
      }

      if (applicantUniqueLink !== null && dasherId === null) {
        await loadApplication(applicantUniqueLink)
        setIsLoading(false)
      }

      // If unique link and dasher ID are already loaded,
      if (applicantUniqueLink && dasherId) {
        setIsLoading(false)
      }
    }

    loadApplicantData()
  }, [applicantUniqueLink, dasherId, loadDasher, loadApplication])

  return (
    <>
      {!isLoading ? (
        <StackChildren>
          <DasherExcitedAnimation />
          <Text styles={Text.Styles.PageTitle1} color={Text.Colors.TextPrimary}>
            <Interpolate
              i18n={i18n}
              i18nKey={i18nKeys.excitement__application_complete__title}
              values={{
                firstName: (application && application.firstName) || 'Dasher',
              }}
              components={[
                <Text
                  color={Text.Colors.TextPrimary}
                  display={Text.DisplayModes.Inline}
                  styles={Text.Styles.PageTitle1}
                />,
              ]}
            />
          </Text>

          <Text
            styles={Text.Styles.Label1}
            color={Text.Colors.TextSecondary}
            weight={Text.Weights.Bold}
          >
            {t.subheading()}
          </Text>
          <Text styles={Text.Styles.Body1} color={Text.Colors.TextSecondary}>
            {t.body()}
          </Text>
          <LinkButton
            type={Button.Types.PrimaryPill}
            size={Button.Sizes.Large}
            onClick={e => {
              e.stopPropagation()
              track(
                DA.EventActions.Clicked,
                DA.ApplicationCompleteNames.ApplicationComplete
              )
            }}
            href={routes.promotionsGetStarted.path}
            data-anchor-id={Tag.Continue}
          >
            {t.primaryAction()}
          </LinkButton>
        </StackChildren>
      ) : (
        <Loading />
      )}
    </>
  )
}

export const mapStateToProps = ({
  errorMessage,
  application,
  applicantUniqueLink,
  dasherId,
}) => ({
  errorMessage,
  application,
  applicantUniqueLink,
  dasherId,
})

const mapDispatchToProps = {
  clearErrors: actions.clearErrors,
  loadApplication: actions.loadApplication,
  loadDasher: actions.loadDasher,
  track: events.trackApplicationCompletePage,
}

const ConnectedApplicationComplete = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ApplicationComplete)
)

export const WrappedApplicationComplete: React.FC<ApplicationCompleteProps &
  ReduxProps> = ({ errorMessage, clearErrors, ...props }) => (
  <Simple
    includeFooter={false}
    headerIsDark={false}
    clearErrors={clearErrors}
    errorMessage={errorMessage}
    type={SimpleStyleType.Standard}
  >
    <Root>
      <Page>
        <PageRoot>
          <ConnectedApplicationComplete {...props} />
        </PageRoot>
      </Page>
    </Root>
  </Simple>
)

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationComplete)
